import { useState, useEffect } from 'react';
import { BackHeader } from '../components/BackHeader';
import { 
  Sun, Cloud, CloudRain, Calendar, CheckCircle2, AlertCircle, 
  Package, Wrench, Users, DollarSign, Bell, TrendingUp, Clock,
  MapPin, Droplets, Wind
} from 'lucide-react';

interface DailyBriefing {
  date: string;
  weather: {
    temp: number;
    condition: 'sunny' | 'cloudy' | 'rainy';
    humidity: number;
    windSpeed: number;
    outfit: string;
  };
  tasks: {
    total: number;
    completed: number;
    pending: number;
    highPriority: number;
  };
  events: {
    time: string;
    title: string;
    type: 'personal' | 'family' | 'work';
  }[];
  alerts: {
    type: 'security' | 'package' | 'maintenance' | 'financial';
    message: string;
    action?: string;
  }[];
  summary: {
    securityStatus: 'armed' | 'disarmed';
    pendingPackages: number;
    unreadAnnouncements: number;
    upcomingPayments: number;
  };
}

export default function DailyDigest() {
  const [briefing, setBriefing] = useState<DailyBriefing>({
    date: new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
    weather: {
      temp: 28,
      condition: 'sunny',
      humidity: 65,
      windSpeed: 12,
      outfit: 'Ropa ligera, posible lluvia por la tarde'
    },
    tasks: {
      total: 8,
      completed: 3,
      pending: 5,
      highPriority: 2
    },
    events: [
      { time: '09:00', title: 'Junta de trabajo', type: 'work' },
      { time: '14:00', title: 'Cita médica - Sofía', type: 'family' },
      { time: '18:00', title: 'Clase de piano - Mateo', type: 'family' },
      { time: '19:30', title: 'Gimnasio', type: 'personal' },
    ],
    alerts: [
      { type: 'package', message: 'Tienes 1 paquete esperando en recepción', action: 'Ver detalles' },
      { type: 'maintenance', message: 'Mantenimiento del elevador mañana 10:00 AM', action: 'Ver más' },
      { type: 'financial', message: 'Pago de mantenimiento vence en 3 días', action: 'Pagar ahora' },
    ],
    summary: {
      securityStatus: 'armed',
      pendingPackages: 1,
      unreadAnnouncements: 2,
      upcomingPayments: 1
    }
  });

  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('¡Buenos días');
    else if (hour < 18) setGreeting('¡Buenas tardes');
    else setGreeting('¡Buenas noches');
  }, []);

  const WeatherIcon = () => {
    switch (briefing.weather.condition) {
      case 'sunny': return <Sun className="w-8 h-8 text-yellow-400" />;
      case 'cloudy': return <Cloud className="w-8 h-8 text-slate-400" />;
      case 'rainy': return <CloudRain className="w-8 h-8 text-blue-400" />;
      default: return <Sun className="w-8 h-8 text-yellow-400" />;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'personal': return 'bg-purple-500/20 text-purple-400';
      case 'family': return 'bg-pink-500/20 text-pink-400';
      case 'work': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'security': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'package': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'maintenance': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'financial': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  return (
    <div className="space-y-6">
      <BackHeader fallback="/" />
      {/* Header */}
      <div className="text-center py-6">
        <p className="text-slate-400 text-sm">{briefing.date}</p>
        <h1 className="text-3xl font-bold text-[var(--v-text-primary)] mt-1">{greeting}, Carlos!</h1>
        <p className="text-slate-400 mt-2">Aquí está tu resumen del día</p>
      </div>

      {/* Weather Card */}
      <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 p-6 rounded-2xl border border-blue-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <WeatherIcon />
            <div>
              <p className="text-4xl font-bold text-white">{briefing.weather.temp}°C</p>
              <p className="text-blue-200 text-sm">{briefing.weather.outfit}</p>
            </div>
          </div>
          <div className="text-right space-y-1">
            <div className="flex items-center gap-2 text-slate-300 text-sm">
              <Droplets className="w-4 h-4" />
              {briefing.weather.humidity}% humedad
            </div>
            <div className="flex items-center gap-2 text-slate-300 text-sm">
              <Wind className="w-4 h-4" />
              {briefing.weather.windSpeed} km/h viento
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Tareas</span>
          </div>
          <p className="text-2xl font-bold text-white">{briefing.tasks.pending}</p>
          <p className="text-slate-500 text-xs">{briefing.tasks.highPriority} urgentes</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Eventos</span>
          </div>
          <p className="text-2xl font-bold text-white">{briefing.events.length}</p>
          <p className="text-slate-500 text-xs">Hoy</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-5 h-5 text-orange-400" />
            <span className="text-slate-400 text-sm">Paquetes</span>
          </div>
          <p className="text-2xl font-bold text-white">{briefing.summary.pendingPackages}</p>
          <p className="text-slate-500 text-xs">En recepción</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Pagos</span>
          </div>
          <p className="text-2xl font-bold text-white">{briefing.summary.upcomingPayments}</p>
          <p className="text-slate-500 text-xs">Pendientes</p>
        </div>
      </div>

      {/* Security Status */}
      <div className={`p-4 rounded-xl border flex items-center justify-between ${
        briefing.summary.securityStatus === 'armed' 
          ? 'bg-green-900/20 border-green-500/30' 
          : 'bg-yellow-900/20 border-yellow-500/30'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            briefing.summary.securityStatus === 'armed' ? 'bg-green-500' : 'bg-yellow-500'
          }`}>
            {briefing.summary.securityStatus === 'armed' ? (
              <CheckCircle2 className="w-6 h-6 text-white" />
            ) : (
              <AlertCircle className="w-6 h-6 text-white" />
            )}
          </div>
          <div>
            <p className="text-white font-semibold">
              Sistema {briefing.summary.securityStatus === 'armed' ? 'Armado' : 'Desarmado'}
            </p>
            <p className="text-slate-400 text-sm">
              {briefing.summary.securityStatus === 'armed' 
                ? 'Tu hogar está protegido' 
                : 'Recuerda armar al salir'}
            </p>
          </div>
        </div>
        <button className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm">
          Verificar
        </button>
      </div>

      {/* Alerts */}
      {briefing.alerts.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-yellow-400" />
            Alertas Importantes
          </h3>
          {briefing.alerts.map((alert, idx) => (
            <div key={idx} className={`p-4 rounded-xl border ${getAlertColor(alert.type)}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {alert.type === 'package' && <Package className="w-5 h-5 mt-0.5" />}
                  {alert.type === 'maintenance' && <Wrench className="w-5 h-5 mt-0.5" />}
                  {alert.type === 'financial' && <DollarSign className="w-5 h-5 mt-0.5" />}
                  {alert.type === 'security' && <AlertCircle className="w-5 h-5 mt-0.5" />}
                  <p className="text-sm">{alert.message}</p>
                </div>
                {alert.action && (
                  <button className="text-sm font-medium hover:underline whitespace-nowrap ml-4">
                    {alert.action}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Today's Schedule */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        <div className="p-4 border-b border-[var(--v-surface-3)]">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Tu Horario de Hoy
          </h3>
        </div>
        <div className="divide-y divide-slate-700">
          {briefing.events.map((event, idx) => (
            <div key={idx} className="p-4 flex items-center gap-4">
              <span className="text-slate-400 font-mono w-16">{event.time}</span>
              <span className={`px-2 py-1 rounded text-xs ${getEventColor(event.type)}`}>
                {event.type === 'personal' ? 'Personal' : event.type === 'family' ? 'Familia' : 'Trabajo'}
              </span>
              <span className="text-white flex-1">{event.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
        <h3 className="font-semibold text-white mb-4">Acciones Rápidas</h3>
        <div className="grid grid-cols-2 gap-3">
          <button className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Ver Reportes
          </button>
          <button className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Mis Tareas
          </button>
          <button className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
            <Users className="w-4 h-4" />
            Proveedores
          </button>
          <button className="bg-orange-600 hover:bg-orange-700 text-white p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
            <MapPin className="w-4 h-4" />
            Amenidades
          </button>
        </div>
      </div>

      {/* Footer Tip */}
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 p-4 rounded-xl border border-purple-500/20">
        <p className="text-slate-300 text-sm">
          <span className="text-purple-400 font-semibold">💡 Consejo del día:</span>{' '}
          Revisa tus plantas, el calor de hoy puede afectarlas. Considera regarlas esta tarde.
        </p>
      </div>
    </div>
  );
}
